<template>
  <div class="p-4">
    <!-- Action Header -->
    <div class="flex justify-end mb-4 border-b pb-4 no-print">
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            color="primary"
            small
            depressed
            v-bind="attrs"
            v-on="on"
            :loading="loading"
            class="font-weight-black rounded-sm h-9 px-6 uppercase tracking-widest"
          >
            <v-icon left size="18">mdi-download</v-icon>
            Download SOA
            <v-icon right size="16">mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="generateOldPDF">
            <v-list-item-icon class="mr-2 my-1">
              <v-icon size="16" color="primary">mdi-download</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-xs font-weight-medium"
                >Standard Template</v-list-item-title
              >
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="generateDetailedPDF">
            <v-list-item-icon class="mr-2 my-1">
              <v-icon size="16" color="success">mdi-download</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-xs font-weight-medium"
                >Detailed Template</v-list-item-title
              >
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- SOA Preview Area -->
    <div id="soa-content" class="soa-container">
      <!-- Header Row -->
      <div class="flex justify-between items-start mb-6">
        <div class="text-[0.7rem] italic leading-tight pt-2">
          Statement of Account as on {{ $dayjs().format('DD-MM-YYYY') }}
        </div>

        <!-- Logo Image or Text-based Logo following Foreclosure style -->
        <div class="text-right">
          <div v-if="orgLogoUrl" style="max-height: 50px; max-width: 150px; overflow: hidden; display: inline-block;">
            <img
              :src="orgLogoUrl"
              alt="Logo"
              style="max-height: 50px; max-width: 150px; object-fit: contain;"
              crossorigin="anonymous"
            />
          </div>
          <div v-else>
            <div
              class="font-bold text-[#1e3a8a]"
              style="
                font-family: serif;
                letter-spacing: -1px;
                font-size: 26px;
                line-height: 1;
                text-transform: uppercase;
              "
            >
              {{ orgNameParts[0] }}
            </div>
            <div
              class="text-right tracking-[0.3em] text-[#1e40af]"
              style="
                margin-top: 2px;
                font-size: 10px;
                text-transform: uppercase;
                font-family: sans-serif;
              "
            >
              {{ orgNameParts[1] }}
            </div>
          </div>
        </div>
      </div>

      <div class="divider mb-4"></div>

      <!-- Company Section -->
      <div class="company-section mb-6">
        <h2 class="text-md font-bold uppercase">{{ computedOrgName }}</h2>
        <p class="text-[10px] leading-tight">Registered Office:- {{ computedRegOfficeAddress }}</p>
        <p class="text-[10px] leading-tight" v-if="computedBranchAddress">
          Branch Office:- {{ computedBranchAddress }}
        </p>
        <p class="text-[10px] leading-tight font-semibold mt-1">CIN:- U67100MH2017PTC292183</p>
      </div>

      <div class="divider mb-4"></div>

      <!-- Borrower & Guarantor Details -->
      <div class="details-section mb-6">
        <div class="mb-4">
          <h3 class="details-title">Borrower Details:-</h3>
          <div class="details-content">
            <div class="flex">
              <span class="label">Borrower Name:-</span>
              <span class="value font-bold">{{ selectedCase.customer?.customerFullName }}</span>
            </div>
            <div class="flex items-start">
              <span class="label">Addrss:-</span>
              <span class="value">{{ borrowerAddress }}</span>
            </div>
            <div class="flex">
              <span class="label">Mob No:-</span>
              <span class="value">{{
                selectedCase.customer?.customerPhoneNumber ||
                (selectedCase.customer?.customerMobile && selectedCase.customer?.customerMobile[0])
              }}</span>
            </div>
          </div>
        </div>

        <div class="divider-thin mb-4"></div>

        <div class="mb-4" v-if="guarantor">
          <h3 class="details-title">Guarantor Details:-</h3>
          <div class="details-content">
            <div class="flex">
              <span class="label">Guarantor Name:-</span>
              <span class="value font-bold">{{ guarantor.customerFullName }}</span>
            </div>
            <div class="flex items-start">
              <span class="label">Addrss:-</span>
              <span class="value">{{ guarantorAddress }}</span>
            </div>
            <div class="flex">
              <span class="label">Mob No:-</span>
              <span class="value">{{
                guarantor.customerPhoneNumber ||
                (guarantor.customerMobile && guarantor.customerMobile[0])
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="divider mb-4"></div>

      <!-- Loan Summary Table -->
      <div class="mb-8">
        <h3 class="summary-title mb-2">Summary of Loan Details for {{ selectedCase.caseNo }} :-</h3>
        <table class="summary-table w-full border-collapse border border-black">
          <tbody>
            <tr>
              <td class="w-1/4 px-2 py-2 border border-black font-semibold">Loan Amount:-</td>
              <td class="w-1/4 px-2 py-2 border border-black">
                {{ formatCurrency(selectedCase.loanDetails?.loanAmountSanctioned || 0) }}
              </td>
              <td class="w-1/4 px-2 py-2 border border-black font-semibold">
                Loan Sanction Date:-
              </td>
              <td class="w-1/4 px-2 py-2 border border-black">
                {{ formatDate(selectedCase.loanDetails?.sanctionDate) }}
              </td>
            </tr>
            <tr>
              <td class="px-2 py-2 border border-black font-semibold">Inerest Amount:-</td>
              <td class="px-2 py-2 border border-black">{{ formatCurrency(totalInterest) }}</td>
              <td class="px-2 py-2 border border-black font-semibold">Emi Start Date:-</td>
              <td class="px-2 py-2 border border-black">
                {{ formatDate(selectedCase.loanDetails?.emiStartDate) }}
              </td>
            </tr>
            <tr>
              <td class="px-2 py-2 border border-black font-semibold">Tenure:-</td>
              <td class="px-2 py-2 border border-black">{{ emiSchedule.length }} Months</td>
              <td class="px-2 py-2 border border-black font-semibold">Emi End Date:-</td>
              <td class="px-2 py-2 border border-black">{{ lastEmiDate }}</td>
            </tr>
            <tr>
              <td class="px-2 py-2 border border-black font-semibold">Interest Rate:-</td>
              <td class="px-2 py-2 border border-black">
                {{ selectedCase.loanDetails?.interestRate }}%
              </td>
              <td class="px-2 py-2 border border-black font-semibold">Frequency:-</td>
              <td class="px-2 py-2 border border-black">
                {{ selectedCase.loanDetails?.emiFrequency || 'Monthly' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Transaction Table Section -->
      <div class="text-center mb-4">
        <h2 class="text-[#00A1E4] text-xl font-normal underline decoration-1 underline-offset-4">
          II - Statement of Accounts
        </h2>
        <p class="text-sm font-semibold mt-1">
          For the period ending {{ $dayjs().format('DD-MM-YYYY') }}
        </p>
        <p
          class="text-[10px] font-bold uppercase mt-1 tracking-widest border-b border-black inline-block pb-0.5"
        >
          Billed Transactions
        </p>
      </div>

      <div class="">
        <table class="transaction-table w-full border-collapse text-[11px]">
          <thead>
            <tr class="border-y border-black">
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Date</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Transaction</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Voucher No</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Instrument No</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Remarks</th>
              <th class="border-x border-black px-2 py-2 text-right bg-gray-50">Due</th>
              <th class="border-x border-black px-2 py-2 text-right bg-gray-50">Receipts</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in flattenedTransactions" :key="i" class="border-b border-gray-300">
              <td class="border-x border-black px-2 py-1.5">{{ formatDate(row.date) }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.transaction }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.voucherNo }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.instrumentNo }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.remarks }}</td>
              <td class="border-x border-black px-2 py-1.5 text-right">
                {{ row.due > 0 ? formatCurrency(row.due) : '' }}
              </td>
            </tr>

            <!-- LPC Due Row -->
            <tr v-if="lpiTotal.due > 0" class="border-b border-black font-semibold">
              <td class="border-x border-black px-2 py-1.5">
                {{ $dayjs().format('DD-MM-YYYY') }}
              </td>
              <td class="border-x border-black px-2 py-1.5" colspan="3">LPC Due</td>
              <td class="border-x border-black px-2 py-1.5"></td>
              <td class="border-x border-black px-2 py-1.5 text-right">
                {{ formatCurrency(lpiTotal.due) }}
              </td>
              <td class="border-x border-black px-2 py-1.5 text-right"></td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="font-bold text-[#00A1E4] border-y border-black">
              <td colspan="5" class="border-x border-black px-2 py-2 text-center uppercase">
                Total
              </td>
              <td class="border-x border-black px-2 py-2 text-right">
                {{ formatCurrency(transactionTotals.due) }}
              </td>
              <td class="border-x border-black px-2 py-2 text-right">
                {{ formatCurrency(transactionTotals.receipts) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- LPC Break Up Section -->
      <div class="mb-8" v-if="lpiBreakup.length > 0">
        <h2 class="text-[#00A1E4] text-center text-xl font-normal mb-2">LPC Break Up</h2>
        <table class="transaction-table w-full border-collapse text-[11px]">
          <thead>
            <tr class="border-y border-black">
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">EMI #</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Date</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Total LPC</th>
              <th class="border-x border-black px-2 py-2 text-right bg-gray-50">LPC</th>
              <th class="border-x border-black px-2 py-2 text-right bg-gray-50">Due</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in lpiBreakup" :key="i" class="border-b border-gray-300">
              <td class="border-x border-black px-2 py-1.5">{{ row.emiNo }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ formatDate(row.date) }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.totalLpi }}</td>
              <td class="border-x border-black px-2 py-1.5 text-right">{{ row.receipt }}</td>
              <td class="border-x border-black px-2 py-1.5 text-right font-semibold">
                {{ row.due }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="font-bold text-[#00A1E4]">
              <td colspan="2" class="border-x border-black px-2 py-2 text-center uppercase">
                Total
              </td>
              <td class="border-x border-black px-2 py-2">{{ lpiTotal.totalLpi }}</td>
              <td class="border-x border-black px-2 py-2 text-right">{{ lpiTotal.receipt }}</td>
              <td class="border-x border-black px-2 py-2 text-right">{{ lpiTotal.due }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Total Due Break Up Section -->
      <div class="mb-8">
        <h2 class="text-[#00A1E4] text-center text-xl font-normal mb-2">Total Due Break Up</h2>
        <table class="summary-table w-full border-collapse border border-black">
          <tbody>
            <tr>
              <td class="w-2/3 px-4 py-2 border border-black font-semibold text-center">
                EMI Dues
              </td>
              <td class="w-1/3 px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.emiDues) }}
              </td>
            </tr>
            <tr>
              <td class="px-4 py-2 border border-black font-semibold text-center">LPC Dues</td>
              <td class="px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.lpiDues) }}
              </td>
            </tr>
            <tr>
              <td class="px-4 py-2 border border-black font-semibold text-center">VAS / HL Dues</td>
              <td class="px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.vasDues) }}
              </td>
            </tr>
            <tr>
              <td class="px-4 py-2 border border-black font-semibold text-center">
                Collection Charges
              </td>
              <td class="px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.collectionCharges) }}
              </td>
            </tr>
            <tr class="font-bold text-[#00A1E4]">
              <td class="px-4 py-2 border border-black text-center uppercase">Total</td>
              <td class="px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.total) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import html2pdf from 'html2pdf.js'
import { getFullAddress } from '@/utils/common'

// Initialize pdfmake fonts safely
pdfMake.vfs = pdfFonts && pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts

export default {
  name: 'CustomerSOA',
  props: {
    selectedCase: {
      type: Object,
      required: true
    },
    orgName: {
      type: String,
      default: ''
    },
    branchName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false
    }
  },
  computed: {
    emiSchedule() {
      return this.selectedCase?.loanDetails?.emiSchedule || []
    },
    borrowerMobile() {
      const customer = this.selectedCase?.customer
      return (
        customer?.customerPhoneNumber ||
        (Array.isArray(customer?.customerMobile)
          ? customer.customerMobile[0]
          : customer?.customerMobile || 'N/A')
      )
    },
    computedBranchName() {
      if (this.branchName && this.branchName !== '-') {
        return this.branchName
      }
      const branchVal = this.selectedCase?.branch
      if (branchVal && typeof branchVal === 'object') {
        return branchVal.branchName || branchVal.name || ''
      }
      if (branchVal && typeof branchVal === 'string' && !/^[0-9a-fA-F]{24}$/.test(branchVal)) {
        return branchVal
      }
      return this.selectedCase?.branchName || ''
    },
    borrowerAddress() {
      const customer = this.selectedCase?.customer
      if (!customer) return 'N/A'
      const addr =
        customer.customerAddress && customer.customerAddress.length > 0
          ? customer.customerAddress[0]
          : customer
      return getFullAddress(addr)
    },
    guarantor() {
      return (
        (this.selectedCase?.guarantors && this.selectedCase.guarantors.length > 0
          ? this.selectedCase.guarantors[0]
          : this.selectedCase?.loanDetails?.guarantors?.[0]) || null
      )
    },
    guarantorAddress() {
      if (!this.guarantor) return 'N/A'
      const addr =
        this.guarantor.customerAddress && this.guarantor.customerAddress.length > 0
          ? this.guarantor.customerAddress[0]
          : this.guarantor
      return getFullAddress(addr)
    },
    computedOrgName() {
      const orgData = this.$storage.getUniversal('orgData')
      return orgData?.name || this.orgName || 'ZAVRON FINSERV'
    },
    computedRegOfficeAddress() {
      const orgData = this.$storage.getUniversal('orgData')
      const orgAddr = orgData?.organization_address || {}
      return (
        [
          orgAddr.address_line_1,
          orgAddr.address_line_2,
          orgAddr.city,
          orgAddr.state,
          orgAddr.pincode
        ]
          .filter(Boolean)
          .join(', ') || '-'
      )
    },
    computedBranchAddress() {
      return this.computedBranchName || ''
    },
    orgNameParts() {
      const name = this.computedOrgName
      const parts = name.split(' ')
      if (parts.length > 1) {
        return [parts[0], parts.slice(1).join(' ')]
      }
      return [name, '']
    },
    rawOrgLogoUrl() {
      const orgData = this.$storage?.getUniversal('orgData') || JSON.parse(localStorage.getItem('orgData')) || {}
      if (!orgData.logo) return null
      if (
        orgData.logo.startsWith('http://') ||
        orgData.logo.startsWith('https://') ||
        orgData.logo.startsWith('data:')
      ) {
        return orgData.logo
      }
      return `${process.env.VUE_APP_S3_BASE_URL || 'https://finfi-uat.s3.ap-south-1.amazonaws.com/'}${orgData.logo}`
    },
    orgLogoUrl() {
      if (!this.rawOrgLogoUrl) return null
      const baseUrl = this.$axios?.defaults?.baseURL || 'https://uatapi.myfinfi.com'
      return `${baseUrl}/proxy-image?url=${encodeURIComponent(this.rawOrgLogoUrl)}`
    },
    lpiBreakup() {
      const breakup = []
      const today = this.$dayjs().startOf('day')
      const loanDetails = this.selectedCase?.loanDetails
      const feeType = loanDetails?.lateFeeType || 'Fixed'
      const feeVal =
        loanDetails?.latePaymentFee !== undefined && loanDetails?.latePaymentFee !== null
          ? parseFloat(loanDetails.latePaymentFee)
          : 0

      this.emiSchedule.forEach((emi, index) => {
        if (emi.isDeleted) return

        const activePayments = (emi.emiPayments || []).filter((p) => !p.isDeleted)
        const receipt = activePayments.reduce(
          (sum, p) => sum + (parseFloat(p.latePaymentAmount) || 0),
          0
        )

        let totalLpi = 0
        let lateDays = 0
        const dueDate = this.$dayjs(emi.dueDate).startOf('day')

        const isMonthlyLogic = feeVal === 600

        const calcLpcFee = (days, refDate) => {
          if (!feeVal || days <= 0) return 0
          if (feeType === 'Fixed' || feeType === 'Flat') {
            if (isMonthlyLogic) {
              const diffMonths = this.$dayjs(refDate).diff(dueDate, 'month')
              const targetDatePlusMonths = this.$dayjs(dueDate).add(diffMonths, 'month')
              const extraDays = this.$dayjs(refDate).diff(targetDatePlusMonths, 'day')
              const chargeableMonths = Math.max(1, diffMonths + (extraDays >= 15 ? 1 : 0))
              return feeVal * chargeableMonths
            }
            return feeVal * days
          }
          if (feeType === 'Percentage') {
            const year = this.$dayjs(refDate).year()
            const yearDays = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 366 : 365
            return (parseFloat(emi.emiAmount || 0) * feeVal * (days / yearDays)) / 100
          }
          return 0
        }

        if (emi.emiStatus === 'Paid') {
          // Paid on time — no LPC
          totalLpi = 0
          lateDays = 0
        } else if (
          emi.emiStatus === 'Late' ||
          emi.emiStatus === 'Late Paid' ||
          emi.emiStatus === 'Minimum Amount Paid'
        ) {
          // Paid but after due date — calculate from actual payment date
          const payDate = emi.paymentDate
            ? this.$dayjs(emi.paymentDate).startOf('day')
            : activePayments[0]?.date
            ? this.$dayjs(activePayments[0].date).startOf('day')
            : dueDate
          lateDays = Math.max(0, payDate.diff(dueDate, 'day'))
          totalLpi = calcLpcFee(lateDays, payDate.toDate())
        } else {
          // Pending / Overdue: calculate from today
          if (today.isAfter(dueDate)) {
            lateDays = today.diff(dueDate, 'day')
            totalLpi = calcLpcFee(lateDays, today.toDate())
          }
        }

        totalLpi = Math.max(totalLpi, receipt)

        if (totalLpi > 0 || receipt > 0) {
          breakup.push({
            emiNo: index + 1,
            date: emi.dueDate,
            totalLpi: totalLpi,
            receipt: receipt,
            due: Math.max(0, totalLpi - receipt),
            lateDays: lateDays
          })
        }
      })

      return breakup
    },
    lpiTotal() {
      return this.lpiBreakup.reduce(
        (acc, row) => {
          acc.totalLpi += row.totalLpi
          acc.receipt += row.receipt
          acc.due += row.due
          return acc
        },
        { totalLpi: 0, receipt: 0, due: 0 }
      )
    },
    totalDueBreakdown() {
      const emiDues = this.emiSchedule
        .filter((emi) => !['Paid', 'Late', 'Late Paid'].includes(emi.emiStatus))
        .reduce((sum, emi) => {
          const emiPaid = (emi.emiPayments || [])
            .filter((p) => !p.isDeleted)
            .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)
          return sum + Math.max(0, (parseFloat(emi.emiAmount) || 0) - emiPaid)
        }, 0)

      const lpiDues = this.lpiTotal.due

      const vasDues = (this.selectedCase?.loanDetails?.additionalCharges || [])
        .filter(
          (c) =>
            !c.isDeleted &&
            !c.isDeduction &&
            c.chargeType === 'CHARGE' &&
            !['Paid', 'Fully Paid'].includes(c.status)
        )
        .reduce((sum, charge) => {
          const net =
            (parseFloat(charge.amount) || 0) -
            (parseFloat(charge.metaData?.discountAmount) || 0) -
            (parseFloat(charge.metaData?.waiverAmount) || 0)
          const unpaid = Math.max(0, net - (parseFloat(charge.paidAmount) || 0))
          return sum + unpaid
        }, 0)

      const collectionCharges = this.emiSchedule.reduce((sum, emi) => {
        const unpaidCollection = (emi.emiPayments || [])
          .filter((p) => !p.isDeleted)
          .reduce((pSum, p) => pSum + (parseFloat(p.collectionCharges) || 0), 0)
        // Usually collection charges are added as dues then paid.
        // If we want "dues", we might need to know if they were actually billed.
        // For now, setting to 0 or sum of unpaid if logic exists.
        return sum + 0
      }, 0)

      return {
        emiDues,
        lpiDues,
        vasDues,
        collectionCharges,
        total: emiDues + lpiDues + vasDues + collectionCharges
      }
    },
    transactionTotals() {
      const transDue = this.flattenedTransactions.reduce((sum, row) => sum + (row.due || 0), 0)
      const transReceipts = this.flattenedTransactions.reduce(
        (sum, row) => sum + (row.receipt || 0),
        0
      )
      return {
        due: transDue + this.lpiTotal.due,
        receipts: transReceipts
      }
    },
    totalRepayable() {
      return this.emiSchedule.reduce((sum, emi) => sum + (emi.emiAmount || 0), 0)
    },
    totalInterest() {
      const principal = parseFloat(this.selectedCase.loanDetails?.loanAmountSanctioned || 0)
      const interest = this.emiSchedule.reduce((sum, emi) => sum + (emi.interestAmount || 0), 0)
      return interest > 0 ? interest : this.totalRepayable - principal
    },
    lastEmiDate() {
      if (!this.emiSchedule.length) return 'N/A'
      return this.formatDate(this.emiSchedule[this.emiSchedule.length - 1].dueDate)
    },
    flattenedTransactions() {
      const rows = []
      const schedule = this.emiSchedule || []

      schedule.forEach((emi, index) => {
        // Installment Row
        // Only show installment row if it's not a dummy/zero entry
        if (parseFloat(emi.emiAmount) > 1) {
          const isPaidEmi = String(emi.emiStatus || '').toLowerCase() === 'paid'
          rows.push({
            date: emi.dueDate,
            transaction: isPaidEmi ? `Installment (EMI #${index + 1})` : `Due (EMI #${index + 1})`,
            voucherNo: '',
            instrumentNo: '',
            remarks: '',
            due: parseFloat(emi.emiAmount) || 0,
            receipt: 0,
            isInstallment: true
          })
        }

        // Detailed Payments
        if (emi.emiPayments && emi.emiPayments.length) {
          const activePayments = emi.emiPayments.filter((p) => !p.isDeleted)
          activePayments.forEach((payment) => {
            // Add Late Payment Amount as a charge row if exists
            if (parseFloat(payment.latePaymentAmount) > 0) {
              rows.push({
                date: payment.date,
                transaction: `Late Fee Charges (EMI #${index + 1})`,
                voucherNo: payment.voucherNo || payment.receiptNo || payment.transactionId || '',
                instrumentNo: payment.utrNo || payment.chequeNo || '',
                remarks: 'Late Payment Penalty',
                due: parseFloat(payment.latePaymentAmount),
                receipt: 0,
                isCharge: true
              })
            }

            // Add Collection Charges as a charge row if exists
            if (parseFloat(payment.collectionCharges) > 0) {
              rows.push({
                date: payment.date,
                transaction: `Collection Charges (EMI #${index + 1})`,
                voucherNo: payment.voucherNo || payment.receiptNo || payment.transactionId || '',
                instrumentNo: payment.utrNo || payment.chequeNo || '',
                remarks: 'Field Collection Charges',
                due: parseFloat(payment.collectionCharges),
                receipt: 0,
                isCharge: true
              })
            }

            rows.push({
              date: payment.date,
              transaction: `EMI Receipt (EMI #${index + 1})`,
              voucherNo: payment.voucherNo || payment.receiptNo || payment.transactionId || '',
              instrumentNo: payment.utrNo || payment.chequeNo || '',
              remarks: payment.paymentMode || 'Payment',
              due: 0,
              receipt: parseFloat(payment.amount) || 0,
              isPayment: true
            })
          })
        } else if (emi.emiStatus?.toLowerCase() === 'paid' || emi.paidAmount > 0) {
          // Legacy payment support
          rows.push({
            date: emi.paymentDate || emi.paidDate || emi.dueDate,
            transaction: `EMI Receipt (EMI #${index + 1})`,
            voucherNo: emi.voucherNo || emi.receiptNo || emi.transactionId || '',
            instrumentNo: emi.chequeNo || emi.utrNo || '',
            remarks: emi.paymentMode || 'Legacy Payment',
            due: 0,
            receipt: parseFloat(emi.paidAmount) || parseFloat(emi.emiAmount) || 0,
            isPayment: true
          })
        }
      })

      // Additional Charges (VAS)
      const additionalCharges = this.selectedCase?.loanDetails?.additionalCharges || []
      additionalCharges.forEach((charge) => {
        const netAmount =
          (parseFloat(charge.amount) || 0) -
          (parseFloat(charge.metaData?.discountAmount) || 0) -
          (parseFloat(charge.metaData?.waiverAmount) || 0)

        rows.push({
          date: charge.dueDate || this.selectedCase.loanDetails?.sanctionDate,
          transaction: charge.name || charge.feeType || 'Additional Charge',
          voucherNo: '',
          instrumentNo: '',
          remarks: charge.remarks || 'VAS/Additional Charge',
          due: netAmount,
          receipt: 0,
          isCharge: true
        })
      })

      // Sort by date then by type (Installment/Charge before Payment if on same date)
      return rows.sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB
        }
        // Priority: Installment (1), Charge (2), Receipt (3)
        const typeA = a.isInstallment ? 1 : a.isCharge ? 2 : 3
        const typeB = b.isInstallment ? 1 : b.isCharge ? 2 : 3
        return typeA - typeB
      })
    }
  },
  methods: {
    formatCurrency(value) {
      if (typeof value !== 'number') value = parseFloat(value) || 0
      if (value === 0) return '0.00'
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return this.$dayjs(date).format('DD-MM-YYYY')
    },
    getAddress(person) {
      if (!person) return 'N/A'
      const addr =
        person.customerAddress && person.customerAddress.length > 0
          ? person.customerAddress[0]
          : person
      return getFullAddress(addr)
    },
    getMobile(person) {
      if (!person) return 'N/A'
      return (
        person.customerPhoneNumber ||
        (Array.isArray(person.customerMobile)
          ? person.customerMobile[0]
          : person.customerMobile || 'N/A')
      )
    },
    getCombinedSOADescription(row) {
      if (row.isInstallment) {
        const match = row.transaction.match(/\d+/)
        const instNo = match ? match[0] : '1'
        return `Installment Amount Due Installment No - ${instNo}`
      }
      if (row.isCharge) {
        let desc = row.transaction || 'Charges'
        if (row.remarks) desc += ` - ${row.remarks}`
        if (row.voucherNo && row.voucherNo !== '0' && row.voucherNo !== '-') {
          desc += `, Receipt No - ${row.voucherNo}`
        }
        if (row.instrumentNo && row.instrumentNo !== '0' && row.instrumentNo !== '-') {
          desc += `, Instrument No - ${row.instrumentNo}`
        }
        return desc
      }
      if (row.isPayment) {
        const mode = row.remarks || 'Bank'
        const instNo = row.instrumentNo || '0'
        const instAmt = this.formatCurrency(row.receipt)
        const txnDate = this.formatDate(row.date)
        const receiptNo = row.voucherNo || '0'
        return `Amount Received Mode - ${mode}, Instrument No - ${instNo}, Instrument Amount - ${instAmt}, Txn Date - ${txnDate}, Receipt No - ${receiptNo}`
      }
      let desc = row.transaction || ''
      if (row.remarks) desc += ` - ${row.remarks}`
      return desc
    },
    async generateOldPDF() {
      try {
        this.loading = true
        const element = document.getElementById('soa-content')
        const originalMaxW = element.style.maxWidth
        element.style.maxWidth = '100%'

        const opt = {
          margin: [10, 10, 10, 10],
          filename: `SOA_${this.selectedCase.caseNo}_${this.selectedCase.customer?.customerFullName}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 3, useCORS: true, letterRendering: true, scrollY: 0 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        }

        await html2pdf().set(opt).from(element).save()
        element.style.maxWidth = originalMaxW
      } catch (error) {
        console.error('PDF Generation Error:', error)
        this.$toast.error('Failed to generate PDF')
      } finally {
        this.loading = false
      }
    },
    async getBase64ImageFromURL(url) {
      try {
        const proxyUrl = `/proxy-image?url=${encodeURIComponent(url)}`
        const response = await this.$axios.get(proxyUrl, { responseType: 'blob' })
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(response.data)
        })
      } catch (err) {
        console.error('Failed to convert logo to base64:', err)
        return null
      }
    },
    async generateDetailedPDF() {
      try {
        this.loading = true
        let logoUrl = this.rawOrgLogoUrl
        if (logoUrl && logoUrl.startsWith('http')) {
          logoUrl = await this.getBase64ImageFromURL(logoUrl)
        }

        const customer = this.selectedCase?.customer
        const emiSchedule = this.selectedCase?.loanDetails?.emiSchedule || []
        const guarantors =
          (this.selectedCase?.guarantors && this.selectedCase.guarantors.length > 0
            ? this.selectedCase.guarantors
            : this.selectedCase?.loanDetails?.guarantors) || []

        const borrowerMobile = this.borrowerMobile
        const borrowerAddress = this.borrowerAddress

        const loanDate = this.selectedCase.loanDetails?.sanctionDate
          ? this.formatDate(this.selectedCase.loanDetails.sanctionDate)
          : this.selectedCase.loanDetails?.emiStartDate
          ? this.formatDate(this.selectedCase.loanDetails.emiStartDate)
          : '-'
        const lastEmiDate = this.lastEmiDate

        const disbursements = this.selectedCase.loanDetails?.loanDisbursements || []
        const disbursedAmount = disbursements.length
          ? disbursements.reduce((sum, d) => sum + (d.amount || 0), 0)
          : this.selectedCase.loanDetails?.disbursedAmount ||
            this.selectedCase.loanDetails?.loanAmountSanctioned ||
            0

        const loanStatus =
          this.selectedCase.loanDetails?.currentStatus || this.selectedCase.status || 'Disbursed'

        let lastPaidDate = '-'
        const payments = []
        emiSchedule.forEach((emi) => {
          if (emi.emiPayments) {
            emi.emiPayments.forEach((p) => {
              if (!p.isDeleted && p.date) {
                payments.push(new Date(p.date))
              }
            })
          } else if (emi.paymentDate) {
            payments.push(new Date(emi.paymentDate))
          }
        })
        if (payments.length > 0) {
          const maxDate = new Date(Math.max(...payments))
          lastPaidDate = this.$dayjs(maxDate).format('DD-MM-YYYY')
        }

        const totalCount = emiSchedule.length
        const paidCount = emiSchedule.filter(
          (e) => e.emiStatus === 'Paid' || e.emiStatus === 'Late Paid'
        ).length

        const unpaidEmis = emiSchedule.filter((e) => !['Paid', 'Late Paid'].includes(e.emiStatus))
        const overdueCount = unpaidEmis.filter((e) => new Date(e.dueDate) < new Date()).length
        const overdueAmount = unpaidEmis.reduce((sum, e) => {
          const emiPaid = (e.emiPayments || [])
            .filter((p) => !p.isDeleted)
            .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)
          return sum + Math.max(0, (e.emiAmount || 0) - emiPaid)
        }, 0)

        const principalPaid = emiSchedule
          .filter((e) => e.emiStatus === 'Paid' || e.emiStatus === 'Late Paid')
          .reduce((sum, e) => sum + (e.principalAmount || 0), 0)
        const principalOs = Math.max(
          0,
          (this.selectedCase.loanDetails?.loanAmountSanctioned || 0) - principalPaid
        )

        const principalOverdue = unpaidEmis.reduce((sum, e) => sum + (e.principalAmount || 0), 0)
        const interestOverdue = unpaidEmis.reduce((sum, e) => sum + (e.interestAmount || 0), 0)

        const lpcDues = this.lpiTotal.due
        const chargesDues = this.totalDueBreakdown.vasDues
        const totalDues = overdueAmount + chargesDues + lpcDues

        const nextEmi = emiSchedule.find(
          (e) => !['Paid', 'Late Paid'].includes(e.emiStatus) && new Date(e.dueDate) >= new Date()
        )
        const nextDueDate = nextEmi ? this.formatDate(nextEmi.dueDate) : '-'

        const currentDateTime = this.$dayjs().format('DD-MM-YYYY hh:mm:ss a')
        const branchName = this.computedBranchName
        const orgName = this.computedOrgName
        const regOfficeAddress = this.computedRegOfficeAddress

        const primaryColor = '#1e3a8a'
        const borderColor = '#9ca3af'
        const headerBg = '#f3f4f6'

        let cumulativeBalance = 0
        const tableBody = [
          [
            { text: 'Date', style: 'ledgerHeader', fillColor: headerBg },
            { text: 'Description', style: 'ledgerHeader', fillColor: headerBg },
            { text: 'Due', style: 'ledgerHeaderRight', fillColor: headerBg },
            { text: 'Receipts', style: 'ledgerHeaderRight', fillColor: headerBg },
            { text: 'Balance', style: 'ledgerHeaderRight', fillColor: headerBg }
          ],
          ...this.flattenedTransactions.map((row) => {
            cumulativeBalance += row.due - row.receipt
            return [
              { text: this.formatDate(row.date), style: 'ledgerCell' },
              { text: this.getCombinedSOADescription(row), style: 'ledgerCell' },
              {
                text: row.due > 0 ? this.formatCurrency(row.due) : '0.00',
                style: 'ledgerCellRight'
              },
              {
                text: row.receipt > 0 ? this.formatCurrency(row.receipt) : '0.00',
                style: 'ledgerCellRight'
              },
              {
                text: `${this.formatCurrency(Math.abs(cumulativeBalance))}(${
                  cumulativeBalance >= 0 ? 'Dr' : 'Cr'
                })`,
                style: 'ledgerCellRightBold'
              }
            ]
          })
        ]

        const totalDue = this.flattenedTransactions.reduce((sum, r) => sum + (r.due || 0), 0)
        const totalReceipts = this.flattenedTransactions.reduce(
          (sum, r) => sum + (r.receipt || 0),
          0
        )
        const finalBal = Math.abs(cumulativeBalance)
        const finalCrDr = cumulativeBalance >= 0 ? 'Dr' : 'Cr'

        tableBody.push([
          { text: 'TOTAL', style: 'ledgerTotalHeader', colSpan: 2, fillColor: headerBg },
          {},
          { text: this.formatCurrency(totalDue), style: 'ledgerTotalRight', fillColor: headerBg },
          {
            text: this.formatCurrency(totalReceipts),
            style: 'ledgerTotalRight',
            fillColor: headerBg
          },
          {
            text: `${this.formatCurrency(finalBal)}(${finalCrDr})`,
            style: 'ledgerTotalRight',
            fillColor: headerBg
          }
        ])

        const docDefinition = {
          pageSize: 'A4',
          pageMargins: [35, 40, 35, 40],
          defaultStyle: {
            font: 'Roboto'
          },
          content: [
            // Top Organization Header inside clean box
            {
              table: {
                widths: ['30%', '30%', '40%'],
                body: [
                  [
                    {
                      stack: logoUrl ? [
                        { image: logoUrl, width: 60, height: 30, fit: [60, 30] }
                      ] : [
                        { text: this.orgNameParts[0], fontSize: 20, bold: true, color: '#1e3a8a' },
                        {
                          text: this.orgNameParts[1],
                          fontSize: 8,
                          bold: true,
                          color: '#1e40af',
                          letterSpacing: 2
                        }
                      ],
                      border: [true, true, false, false]
                    },
                    {
                      text: 'Statement Of Account',
                      alignment: 'center',
                      fontSize: 12,
                      bold: true,
                      color: primaryColor,
                      margin: [0, 8, 0, 0],
                      border: [false, true, false, false]
                    },
                    {
                      stack: [
                        {
                          columns: [
                            { text: 'Loan Number', fontSize: 7.5, bold: true, width: '45%' },
                            {
                              text: `: ${this.selectedCase.caseNo || ''}`,
                              fontSize: 7.5,
                              width: '55%'
                            }
                          ]
                        },
                        {
                          columns: [
                            { text: 'Generated On', fontSize: 7.5, bold: true, width: '45%' },
                            { text: `: ${currentDateTime}`, fontSize: 7.5, width: '55%' }
                          ]
                        },
                        {
                          columns: [
                            { text: 'Product Type', fontSize: 7.5, bold: true, width: '45%' },
                            {
                              text: `: ${
                                this.selectedCase.loanDetails?.caseType || 'Personal Loan'
                              }`,
                              fontSize: 7.5,
                              width: '55%'
                            }
                          ]
                        },
                        {
                          columns: [
                            { text: 'Branch Name', fontSize: 7.5, bold: true, width: '45%' },
                            { text: `: ${branchName || ''}`, fontSize: 7.5, width: '55%' }
                          ]
                        }
                      ],
                      border: [false, true, true, false]
                    }
                  ],
                  [
                    {
                      text: `${orgName}\n${regOfficeAddress}`,
                      fontSize: 7.5,
                      color: '#4b5563',
                      colSpan: 3,
                      margin: [0, 4, 0, 0],
                      border: [true, false, true, true]
                    },
                    {},
                    {}
                  ]
                ]
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#9ca3af',
                vLineColor: () => '#9ca3af',
                paddingLeft: () => 10,
                paddingRight: () => 10,
                paddingTop: () => 6,
                paddingBottom: () => 6
              },
              margin: [0, 0, 0, 12]
            },

            // Borrower Details & Loan Details side-by-side
            {
              table: {
                widths: ['49%', '2%', '49%'],
                body: [
                  [
                    {
                      stack: [
                        {
                          text: 'Borrower Details',
                          fontSize: 9.5,
                          bold: true,
                          color: primaryColor,
                          margin: [0, 0, 0, 6]
                        },
                        {
                          columns: [
                            { text: 'Name', fontSize: 8, bold: true, width: '35%' },
                            {
                              text: `: ${customer?.customerFullName || ''}`,
                              fontSize: 8,
                              width: '65%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Father/Spouse Name', fontSize: 8, bold: true, width: '35%' },
                            {
                              text: `: ${customer?.fatherOrSpouseName || '-'}`,
                              fontSize: 8,
                              width: '65%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Mobile No', fontSize: 8, bold: true, width: '35%' },
                            { text: `: ${borrowerMobile}`, fontSize: 8, width: '65%' }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Email ID', fontSize: 8, bold: true, width: '35%' },
                            {
                              text: `: ${customer?.customerEmail || '-'}`,
                              fontSize: 8,
                              width: '65%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Address', fontSize: 8, bold: true, width: '35%' },
                            {
                              text: `: ${borrowerAddress}`,
                              fontSize: 8,
                              width: '65%',
                              lineHeight: 1.1
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        }
                      ],
                      border: [true, true, false, true]
                    },
                    {
                      canvas: [
                        {
                          type: 'line',
                          x1: 0,
                          y1: 0,
                          x2: 0,
                          y2: 70,
                          lineWidth: 0.5,
                          strokeColor: '#9ca3af'
                        }
                      ],
                      border: [false, true, false, true]
                    },
                    {
                      stack: [
                        {
                          text: 'Loan Details',
                          fontSize: 9.5,
                          bold: true,
                          color: primaryColor,
                          margin: [0, 0, 0, 6]
                        },
                        {
                          columns: [
                            { text: 'Loan No', fontSize: 8, bold: true, width: '35%' },
                            {
                              text: `: ${this.selectedCase.caseNo || ''}`,
                              fontSize: 8,
                              width: '65%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Loan Date', fontSize: 8, bold: true, width: '35%' },
                            { text: `: ${loanDate}`, fontSize: 8, width: '65%' }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Loan Amount', fontSize: 8, bold: true, width: '35%' },
                            {
                              text: `: ${this.formatCurrency(
                                this.selectedCase.loanDetails?.loanAmountSanctioned
                              )}`,
                              fontSize: 8,
                              width: '65%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Disbursed Amount', fontSize: 8, bold: true, width: '35%' },
                            {
                              text: `: ${this.formatCurrency(disbursedAmount)}`,
                              fontSize: 8,
                              width: '65%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Loan Status', fontSize: 8, bold: true, width: '35%' },
                            { text: `: ${loanStatus}`, fontSize: 8, width: '65%' }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        }
                      ],
                      border: [false, true, true, true]
                    }
                  ]
                ]
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#9ca3af',
                vLineColor: () => '#9ca3af',
                paddingLeft: () => 12,
                paddingRight: () => 12,
                paddingTop: () => 8,
                paddingBottom: () => 8
              },
              margin: [0, 0, 0, 12]
            },

            // Loan Terms & Due Details side-by-side
            {
              table: {
                widths: ['49%', '2%', '49%'],
                body: [
                  [
                    {
                      stack: [
                        {
                          text: 'Loan Terms',
                          fontSize: 9.5,
                          bold: true,
                          color: primaryColor,
                          margin: [0, 0, 0, 6]
                        },
                        {
                          columns: [
                            { text: 'ROI (%) | APR (%)', fontSize: 8, bold: true, width: '40%' },
                            {
                              text: `: ${
                                this.selectedCase.loanDetails?.interestRate || '0.00'
                              } | 0.00`,
                              fontSize: 8,
                              width: '60%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Inst. Amount', fontSize: 8, bold: true, width: '40%' },
                            {
                              text: `: ${this.formatCurrency(emiSchedule[0]?.emiAmount || 0)}`,
                              fontSize: 8,
                              width: '60%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Tenure (M)', fontSize: 8, bold: true, width: '40%' },
                            {
                              text: `: ${
                                this.selectedCase.loanDetails?.tenure || emiSchedule.length || 0
                              }`,
                              fontSize: 8,
                              width: '60%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            {
                              text: 'Instl. Start| End Date',
                              fontSize: 8,
                              bold: true,
                              width: '40%'
                            },
                            {
                              text: `: ${this.formatDate(
                                this.selectedCase.loanDetails?.emiStartDate
                              )} | ${lastEmiDate}`,
                              fontSize: 8,
                              width: '60%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            {
                              text: 'Inst. Freq | Last Paid Date',
                              fontSize: 8,
                              bold: true,
                              width: '40%'
                            },
                            {
                              text: `: ${
                                this.selectedCase.loanDetails?.emiFrequency || 'Monthly'
                              } | ${lastPaidDate}`,
                              fontSize: 8,
                              width: '60%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Advance Amount', fontSize: 8, bold: true, width: '40%' },
                            {
                              text: `: ${this.formatCurrency(
                                this.selectedCase.loanDetails?.advanceAmount || 0
                              )}`,
                              fontSize: 8,
                              width: '60%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'CKYC ID', fontSize: 8, bold: true, width: '40%' },
                            { text: `: ${customer?.ckycId || '-'}`, fontSize: 8, width: '60%' }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        }
                      ],
                      border: [true, true, false, true]
                    },
                    {
                      canvas: [
                        {
                          type: 'line',
                          x1: 0,
                          y1: 0,
                          x2: 0,
                          y2: 95,
                          lineWidth: 0.5,
                          strokeColor: '#9ca3af'
                        }
                      ],
                      border: [false, true, false, true]
                    },
                    {
                      stack: [
                        {
                          text: 'Due Details',
                          fontSize: 9.5,
                          bold: true,
                          color: primaryColor,
                          margin: [0, 0, 0, 6]
                        },
                        {
                          columns: [
                            { text: 'Instl. Paid Count', fontSize: 8, bold: true, width: '45%' },
                            { text: `: ${paidCount} | ${totalCount}`, fontSize: 8, width: '55%' }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            {
                              text: 'Instl. Overdue Count | Amount',
                              fontSize: 8,
                              bold: true,
                              width: '45%'
                            },
                            {
                              text: `: ${overdueCount} | ${this.formatCurrency(overdueAmount)}`,
                              fontSize: 8,
                              width: '55%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Principal O/s', fontSize: 8, bold: true, width: '45%' },
                            {
                              text: `: ${this.formatCurrency(principalOs)}`,
                              fontSize: 8,
                              width: '55%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            {
                              text: 'Principal | Interest Overdue',
                              fontSize: 8,
                              bold: true,
                              width: '45%'
                            },
                            {
                              text: `: ${this.formatCurrency(
                                principalOverdue
                              )} | ${this.formatCurrency(interestOverdue)}`,
                              fontSize: 8,
                              width: '55%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Total Dues', fontSize: 8, bold: true, width: '45%' },
                            {
                              text: `: ${this.formatCurrency(totalDues)}`,
                              fontSize: 8,
                              width: '55%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            {
                              text: 'Charges Dues | LPC Dues',
                              fontSize: 8,
                              bold: true,
                              width: '45%'
                            },
                            {
                              text: `: ${this.formatCurrency(chargesDues)} | ${this.formatCurrency(
                                lpcDues
                              )}`,
                              fontSize: 8,
                              width: '55%'
                            }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        },
                        {
                          columns: [
                            { text: 'Next Due Date', fontSize: 8, bold: true, width: '45%' },
                            { text: `: ${nextDueDate}`, fontSize: 8, width: '55%' }
                          ],
                          margin: [0, 1.5, 0, 1.5]
                        }
                      ],
                      border: [false, true, true, true]
                    }
                  ]
                ]
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#9ca3af',
                vLineColor: () => '#9ca3af',
                paddingLeft: () => 12,
                paddingRight: () => 12,
                paddingTop: () => 8,
                paddingBottom: () => 8
              },
              margin: [0, 0, 0, 12]
            },

            // Customer Details Table
            {
              table: {
                widths: ['15%', '12%', '23%', '35%', '15%'],
                headerRows: 1,
                body: [
                  [
                    { text: 'CIF', style: 'tableHeader', fillColor: headerBg },
                    { text: 'Type', style: 'tableHeader', fillColor: headerBg },
                    { text: 'Name', style: 'tableHeader', fillColor: headerBg },
                    { text: 'Address', style: 'tableHeader', fillColor: headerBg },
                    { text: 'Mobile No', style: 'tableHeader', fillColor: headerBg }
                  ],
                  [
                    { text: customer?.crn || '-', style: 'tableCell' },
                    { text: 'Borrower', style: 'tableCell' },
                    { text: customer?.customerFullName || '', style: 'tableCell' },
                    { text: borrowerAddress, style: 'tableCell' },
                    { text: borrowerMobile, style: 'tableCell' }
                  ],
                  ...guarantors.map((g) => [
                    { text: g.crn || '-', style: 'tableCell' },
                    { text: 'Guarantor', style: 'tableCell' },
                    { text: g.customerFullName || g.name || '', style: 'tableCell' },
                    { text: this.getAddress(g), style: 'tableCell' },
                    { text: this.getMobile(g), style: 'tableCell' }
                  ])
                ]
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#9ca3af',
                vLineColor: () => '#9ca3af',
                paddingLeft: () => 6,
                paddingRight: () => 6,
                paddingTop: () => 5,
                paddingBottom: () => 5
              },
              margin: [0, 0, 0, 12]
            },

            // Linked Loans Box
            {
              table: {
                widths: ['100%'],
                body: [
                  [
                    {
                      text: `Linked Loans : ${this.selectedCase.caseNo || ''}`,
                      fontSize: 7.5,
                      bold: true,
                      margin: [0, 1, 0, 1]
                    }
                  ]
                ]
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#9ca3af',
                vLineColor: () => '#9ca3af',
                paddingLeft: () => 10,
                paddingRight: () => 10,
                paddingTop: () => 4,
                paddingBottom: () => 4
              },
              margin: [0, 0, 0, 12]
            },

            // PAGE BREAK before Transaction Table
            {
              table: {
                widths: ['100%'],
                body: [
                  [
                    {
                      text: `Statement of Accounts From ${loanDate} To ${lastEmiDate}`,
                      alignment: 'center',
                      color: 'white',
                      fillColor: '#1e3a8a',
                      fontSize: 9.5,
                      bold: true,
                      margin: [0, 4, 0, 4]
                    }
                  ]
                ]
              },
              layout: 'noBorders',
              margin: [0, 0, 0, 8],
              pageBreak: 'before'
            },

            // Transaction Table
            {
              table: {
                widths: ['12%', '52%', '12%', '12%', '12%'],
                headerRows: 1,
                body: tableBody
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#9ca3af',
                vLineColor: () => '#9ca3af',
                paddingLeft: () => 6,
                paddingRight: () => 6,
                paddingTop: () => 5,
                paddingBottom: () => 5
              },
              margin: [0, 0, 0, 12]
            },

            // Bottom line divider
            {
              canvas: [
                {
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 525,
                  y2: 0,
                  lineWidth: 0.5,
                  strokeColor: '#9ca3af'
                }
              ],
              margin: [0, 8, 0, 4]
            },
            // Disclaimer
            {
              text: 'Please quote your Loan Number when you contact us. For further clarifications please contact us on 7122520007 or info@zavronfinserv.com',
              alignment: 'center',
              fontSize: 8,
              color: '#4b5563',
              lineHeight: 1.3
            }
          ],
          styles: {
            orgHeaderName: {
              fontSize: 14,
              bold: true,
              color: '#1e3a8a'
            },
            orgHeaderSub: {
              fontSize: 8.5,
              italics: true,
              color: '#4b5563'
            },
            orgTitleCentered: {
              fontSize: 10,
              bold: true,
              alignment: 'center',
              color: '#111827'
            },
            orgAddressText: {
              fontSize: 7.5,
              alignment: 'center',
              color: '#4b5563',
              lineHeight: 1.2
            },
            sectionHeader: {
              fontSize: 10,
              bold: true,
              color: '#1e3a8a',
              margin: [0, 8, 0, 4]
            },
            tableHeader: {
              fontSize: 8.5,
              bold: true,
              color: '#1f2937'
            },
            tableCell: {
              fontSize: 7.5,
              color: '#111827'
            },
            summaryLabel: {
              fontSize: 8,
              bold: true,
              color: '#374151'
            },
            summaryValue: {
              fontSize: 8,
              color: '#111827'
            },
            ledgerHeader: {
              fontSize: 8,
              bold: true,
              color: '#1f2937'
            },
            ledgerHeaderRight: {
              fontSize: 8,
              bold: true,
              color: '#1f2937',
              alignment: 'right'
            },
            ledgerCell: {
              fontSize: 7.5,
              color: '#111827'
            },
            ledgerCellRight: {
              fontSize: 7.5,
              color: '#111827',
              alignment: 'right'
            },
            ledgerCellRightBold: {
              fontSize: 7.5,
              bold: true,
              color: '#111827',
              alignment: 'right'
            },
            ledgerTotalHeader: {
              fontSize: 8,
              bold: true,
              color: '#1f2937',
              alignment: 'right'
            },
            ledgerTotalRight: {
              fontSize: 8,
              bold: true,
              color: '#1f2937',
              alignment: 'right'
            }
          }
        }

        const filename = `SOA_${this.selectedCase?.caseNo || 'Statement'}.pdf`
        pdfMake.createPdf(docDefinition).download(filename)
      } catch (error) {
        console.error('PDF Generation failed:', error)
        this.$toast.error('Failed to generate PDF document')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.soa-container {
  font-family: 'Times New Roman', Times, serif;
  max-width: 800px;
  margin: 0 auto;
  color: #000;
  background: white;
  padding: 10px;
  box-sizing: border-box;
}

.divider {
  border-top: 1.5px solid #000;
}

.divider-thin {
  border-top: 1px solid #000;
}

.details-title {
  font-weight: bold;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-size: 0.85rem;
}

.details-content {
  margin-top: 6px;
  line-height: 1.6;
  font-size: 0.75rem;
}

.details-content .label {
  min-width: 130px;
  font-weight: 500;
}

.summary-title {
  font-weight: bold;
  font-size: 0.85rem;
}

.summary-table td {
  font-size: 0.75rem;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

.transaction-table th {
  font-weight: bold;
  font-size: 0.7rem;
}

.transaction-table td {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

@media print {
  .no-print {
    display: none;
  }
  .soa-container {
    max-width: 100% !important;
    padding: 0;
  }
}
</style>
